import { z } from "zod";
import { zId, zInstanteUtc } from "./dominio.js";
/**
 * Caderno (RN-29) e fontes por categoria (RN-08).
 *
 * `nota.html` é a fonte da verdade (RN-39, ADR 012). O Markdown virou derivado
 * da exportação: a barra de ferramentas do editor — cor, realce, tamanho,
 * alinhamento — não tem equivalente em Markdown, e guardar Markdown obrigaria a
 * perder essa formatação a cada gravação.
 *
 * O conteúdo passa pelo saneador de `conteudo.ts` na gravação e na leitura
 * (RN-41, invariante 17), e quem renderiza monta elementos a partir da árvore —
 * nunca `innerHTML`.
 */
export const zCategoriaResumo = z.object({
    id: zId,
    nome: z.string(),
    /** Caminho completo, do mais raso ao mais fundo. */
    caminho: z.array(z.string()),
    profundidade: z.number().int().nonnegative(),
});
export const zListaDeCategorias = z.object({ categorias: z.array(zCategoriaResumo) });
// ---------------------------------------------------------------- notas
export const zNota = z.object({
    id: zId,
    titulo: z.string(),
    html: z.string(),
    categoriaId: zId.nullable(),
    categoria: z.array(z.string()),
    /** Itens que a nota carrega junto — de onde a dúvida nasceu, por exemplo. */
    itensAnexados: z.array(zId),
    criadoEm: zInstanteUtc,
    atualizadoEm: zInstanteUtc,
});
export const zNotaResumo = z.object({
    id: zId,
    titulo: z.string(),
    categoria: z.array(z.string()),
    /** Começo do texto **puro**: a marcação não ajuda a reconhecer a nota. */
    resumo: z.string(),
    itensAnexados: z.number().int(),
    atualizadoEm: zInstanteUtc,
});
export const zConsultaDeNotas = z.object({
    /** Vazio traz tudo. A busca é no texto puro, não no HTML cru. */
    termo: z.string().trim().default(""),
    categoriaId: zId.nullable().default(null),
});
export const zListaDeNotas = z.object({ notas: z.array(zNotaResumo) });
export const zNotaEntrada = z.object({
    titulo: z.string().trim().min(1, "Dê um título à nota").max(200),
    /**
     * HTML saneado. O limite é do texto **guardado**, então ele conta a marcação
     * junto: uma nota inteira de cor e realce ocupa mais bytes do que a mesma
     * nota em texto puro, e o teto precisa caber nas duas.
     */
    html: z.string().max(400_000),
    categoriaId: zId.nullable().default(null),
    itensAnexados: z.array(zId).default([]),
});
/**
 * Salvar exige dizer **de qual versão** se está partindo.
 *
 * É o que permite detectar o conflito da RN-29: se o servidor mudou desde que
 * a tela carregou, gravar por cima apagaria a outra edição em silêncio.
 */
export const zSalvarNota = zNotaEntrada.extend({
    baseAtualizadoEm: zInstanteUtc,
});
/**
 * Abrir o caderno a partir de um item (RN-43).
 *
 * **A nota abre pela matéria, não pelo item.** Uma nota por item transformaria
 * o caderno numa cópia do acervo, com centenas de páginas de uma linha; o que
 * se escreve com as próprias palavras é sobre o assunto, e o item é só onde a
 * dúvida apareceu. Por isso o alvo é a última página editada daquela matéria.
 *
 * Se não houver nenhuma, uma é criada com o nome da matéria e o item vai
 * anexado — é o vínculo de onde a nota nasceu, e continua sendo o `itensAnexados`
 * que já existia. A `anotacao` presa ao item continua sendo outra coisa, em
 * texto simples (RN-07).
 */
/** Uma outra página da mesma matéria, para o painel poder trocar sem sair. */
export const zPaginaIrma = z.object({
    id: zId,
    titulo: z.string(),
    categoria: z.array(z.string()),
});
export const zNotaDoAssunto = z.object({
    nota: zNota,
    /** `true` quando a nota acabou de ser criada — a tela avisa em vez de surpreender. */
    criada: z.boolean(),
    /** A matéria pela qual a nota foi encontrada, para a tela poder dizer. */
    materia: z.string().nullable(),
    /**
     * O caminho da categoria **do item** — a migalha que o painel mostra.
     *
     * É o do item, e não o da nota encontrada: quando o assunto ainda não tem
     * página e a busca sobe para a matéria, o que orienta quem está respondendo é
     * onde **ele** está, não onde a página acabou sendo achada. A diferença entre
     * os dois é justamente o que a migalha precisa deixar à vista.
     */
    caminho: z.array(z.string()),
    /**
     * A categoria do item, pelo **id** — a pasta em que o painel abre o seletor
     * de pastas (RN-57).
     *
     * O `caminho` acima é rótulo, e casar rótulo com pasta seria a mesma árvore
     * mantida em dois lugares: duas matérias com um "Introdução" cada e o painel
     * criaria a página na errada. O id é o dado.
     */
    categoriaIdDoItem: zId,
    /** As outras páginas da mesma matéria, na ordem da árvore. */
    irmas: z.array(zPaginaIrma),
});
/**
 * O contexto do item para o caderno da sessão: o mesmo que `zNotaDoAssunto`
 * traz, menos a nota. Lê, não cria — a página da sessão só nasce quando a
 * pessoa escreve.
 */
export const zContextoDoItem = zNotaDoAssunto.omit({ nota: true, criada: true });
export const zNotaUma = z.object({ nota: zNota });
/**
 * O resultado de salvar. Conflito **não é erro**: as duas versões voltam, e
 * quem decide é o usuário (RN-29). Por isso vem no corpo de uma resposta de
 * sucesso, e não no canal de erro, que só carrega mensagem.
 */
export const zNotaSalva = z.discriminatedUnion("resultado", [
    z.object({ resultado: z.literal("salvo"), nota: zNota }),
    z.object({
        resultado: z.literal("conflito"),
        /** O que você tentou gravar, devolvido para não se perder. */
        suaVersao: zNotaEntrada.extend({ categoriaId: zId.nullable() }),
        /** O que está no servidor agora. */
        versaoDoServidor: zNota,
    }),
]);
export const zNotaId = z.object({ id: zId });
// --------------------------------------------------------------- fontes
export const TIPOS_FONTE = ["livro", "video", "artigo", "curso", "documentacao", "prova", "material", "outro"];
/** Os tipos que carregam texto próprio, convertido de um arquivo (ADR 043). */
export const TIPOS_FONTE_COM_TEXTO = ["prova", "material"];
export const zTipoFonte = z.enum(TIPOS_FONTE);
export const zFonte = z.object({
    id: zId,
    categoriaId: zId,
    categoria: z.array(z.string()),
    titulo: z.string(),
    url: z.string().nullable(),
    tipo: zTipoFonte,
    /** O nome do tipo como a pessoa escreveu; nulo cai para o nome do `tipo`. */
    rotulo: z.string().nullable(),
    nota: z.string().nullable(),
    /**
     * Tamanho do texto da fonte, em caracteres; 0 quando não tem. O texto em si
     * **não** viaja na lista: ela também vai no feedback do "não entendi"
     * (RN-08), e 60 mil caracteres por fonte ali seriam peso morto. Quem precisa
     * dele pede `fonte.texto`.
     */
    caracteres: z.number().int(),
    /**
     * O que a miniatura mostra no lugar do texto inteiro (ADR 044): a linha que
     * o modelo escreveu, quantas questões ele contou, e o começo do texto para
     * quando não há resumo. Todos nulos em fonte sem texto.
     */
    resumo: z.string().nullable(),
    questoes: z.number().int().nullable(),
    trecho: z.string().nullable(),
    criadoEm: zInstanteUtc,
    atualizadoEm: zInstanteUtc,
});
export const zFonteEntrada = z.object({
    categoriaId: zId,
    titulo: z.string().trim().min(1, "Dê um título à fonte").max(200),
    /** Só http e https: o mesmo critério do link no Markdown. */
    url: z
        .string()
        .trim()
        .refine((v) => v === "" || /^https?:\/\//i.test(v), "O endereço precisa começar com http:// ou https://")
        .transform((v) => (v === "" ? null : v))
        .nullable()
        .default(null),
    tipo: zTipoFonte.default("outro"),
    rotulo: z
        .string()
        .trim()
        .max(40, "O tipo pode ter até 40 caracteres")
        .transform((v) => (v === "" ? null : v))
        .nullable()
        .default(null),
    nota: z
        .string()
        .trim()
        .max(2000)
        .transform((v) => (v === "" ? null : v))
        .nullable()
        .default(null),
    /**
     * O texto da prova ou do material. **Ausente mantém o que está gravado** —
     * editar o título de uma fonte não pode apagar o texto dela; vazio apaga.
     */
    markdown: z
        .string()
        .trim()
        .max(1_000_000)
        .transform((v) => (v === "" ? null : v))
        .nullable()
        .optional(),
});
export const zListaDeFontes = z.object({ fontes: z.array(zFonte) });
/**
 * O texto de uma fonte. O teto de verdade é `NOESIS_FONTE_MAX_TEXTO`, conferido
 * no servidor; este é só o limite de forma, para o corpo não crescer sem fim.
 */
export const zTextoDaFonte = z.object({ markdown: z.string().max(1_000_000) });
/**
 * Um arquivo para converter em Markdown (ADR 043). Viaja em base64 no JSON: o
 * app não tem rota de upload, e uma dependência de multipart por uma rota não
 * se paga. O teto de bytes é conferido no servidor, contra a configuração.
 */
export const zArquivoParaConverter = z.object({
    nome: z.string().trim().max(200),
    tipoMime: z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"]),
    tipo: z.enum(TIPOS_FONTE_COM_TEXTO).default("material"),
    base64: z.string().min(1).max(12_000_000),
});
export const zFonteId = z.object({ id: zId });
// -------------------------------------------------------------- dúvidas
export const zDuvida = z.object({
    id: zId,
    itemId: zId.nullable(),
    categoriaId: zId,
    categoria: z.array(z.string()),
    descricao: z.string().nullable(),
    resolvida: z.boolean(),
    notaId: zId.nullable(),
    /** O enunciado do item que gerou a dúvida, para a lista fazer sentido sozinha. */
    enunciado: z.string().nullable(),
    criadoEm: zInstanteUtc,
});
export const zListaDeDuvidas = z.object({ duvidas: z.array(zDuvida) });
export const zNaoEntendiEntrada = z.object({
    itemId: zId,
    descricao: z
        .string()
        .trim()
        .max(2000)
        .transform((v) => (v === "" ? null : v))
        .nullable()
        .default(null),
});
/**
 * "Não entendi" tem que entregar as três coisas de uma vez (RN-08), senão vira
 * só mais um marcador: a **dúvida** registrada, as **fontes** da categoria, e o
 * **esboço de nota** já com o item anexado.
 */
export const zNaoEntendiResposta = z.object({
    duvida: zDuvida,
    /** Fontes da categoria do item; sobe na árvore quando a folha não tem nenhuma. */
    fontes: z.array(zFonte),
    /** De qual categoria vieram as fontes, quando não foi a do próprio item. */
    fontesHerdadasDe: z.array(z.string()).nullable(),
    /** Já preenchido, pronto para o usuário editar e salvar. */
    esboco: zNotaEntrada.extend({ categoriaId: zId.nullable() }),
});
export const zDuvidaId = z.object({ id: zId });
export const zResolverDuvida = z.object({ notaId: zId.nullable().default(null) });
//# sourceMappingURL=caderno.js.map